import { connect } from 'react-redux';
import { http, SourceGroup } from 'helpers/axios';
import { compose, lifecycle, withState, withHandlers } from 'recompose';
import { createStore, combineReducers } from 'redux';
import _ from 'lodash';

import { getTemplateModuleName } from 'templates';
import TemplateEditor from '../components/TemplateEditor';
import { reducers } from '../reducers';
import { currentGroup, checkPermission } from 'utils/credentials';
import { setProperty } from 'utils/state';
import { ROLES } from '../../../../consts';
import API from '../../../../utils/api';
import { backLink } from 'utils';

const loadTemplate = (editor, data, module) => {
  const { mounted, setProperty } = editor.props;

  if (!mounted) {
    return;
  }

  setProperty(editor, 'editingId', data._id);
  setProperty(editor, 'group', data.group);
  setProperty(editor, 'type', data.type);

  const store = createStore(combineReducers({ ...reducers, ...module.reducers }), {
    templateName: data.name,
    templateIcon: data.icon,
    templateIconUrl: data.iconUrl,

    pointsPerPlay: data.pointsPerPlay,
    pointsPerWin: data.pointsPerWin,

    ...module.preload(data),
  });

  store.subscribe(_.debounce(() => updateTemplate(editor), 50));

  setProperty(editor, 'store', store);
  setProperty(editor, 'module', module);
};

const updateTemplate = editor => {
  const state = editor.props.store.getState();

  const payload = {
    name: state.templateName.trim(),
    icon: state.templateIcon,

    pointsPerPlay: state.pointsPerPlay,
    pointsPerWin: state.pointsPerWin,

    params: editor.props.module.update(state),
  };

  API.editTemplate(editor.props.editingId, payload, editor.props.sources);
};

const mapStateToProps = state => ({
  auth: state.auth,
  roles: _.get(state, 'auth.currentGroup.myRoles', 'default'),
  setProperty,
});

const loadModule = (type, callback) => {
  const moduleName = getTemplateModuleName(type);

  import(`templates/${moduleName}`)
    .then(module => {
      callback(module);
    })
    .catch(err => console.log(err));
};

const defaultTemplate = (type, params) => ({
  name: '',
  icon: '',
  type,
  params,
});

const init = editor => {
  const id = _.get(editor.props, 'match.params.id');

  if (id) {
    API.getTemplate(id, { mediaUrl: true }, editor.props.sources)
      .then(({ data }) => {
        if (data) {
          loadModule(data.type, module => loadTemplate(editor, data, module));

          if (data.standOption && data.standOption.pageMessage) {
            const message = data.standOption;
            let test = message.pageMessage.replace(/<span>/g, '');
            test = test.replace(/<\/span>/g, '\n');
            message.pageMessage = test.trim();
          }

          editor.props.setStandOption(data.standOption || null);
          editor.props.setHasDownloadPage(
            (data.standOption && data.standOption.hasDownloadPage) || false
          );
        }
      })
      .catch(err => console.log(err));
  } else {
    const { pathname, state } = editor.props.location || {};

    if (state && state.type) {
      loadModule(state.type, module => {
        API.createTemplate(
          defaultTemplate(state.type, module.getDefault(state.type)),
          editor.props.sources
        )
          .then(({ data }) => {
            loadTemplate(editor, data, module);
            editor.props.history.push(`${pathname}/${data._id}`, state);

            if (data.standOption && data.standOption.pageMessage) {
              const message = data.standOption;
              let test = message.pageMessage.replace(/<span>/g, '');
              test = test.replace(/<\/span>/g, '\n');
              message.pageMessage = test.trim();
            }

            editor.props.setStandOption(data.standOption);
          })
          .catch(err => {
            console.log(err);
          });
      });
    } else {
      editor.props.history.push(backLink(editor.props));
    }
  }
};

const $lifecycle = lifecycle({
  componentDidMount() {
    const editor = this;

    this._isMounted = true;
    editor.props.setMounted(
      () => () => this._isMounted,
      () => {
        init(editor);
      }
    );
  },

  componentDidUpdate() {
    checkPermission(this, ROLES.TEMPLATE);

    const { group, history } = this.props;

    if (group && group !== currentGroup('_id')) {
      history.push(backLink(this.props));
    }
  },

  componentWillUnmount() {
    this._isMounted = false;
  },
});

const enhance = compose(
  connect(mapStateToProps),
  withState('editingId', 'setEditingId', null),
  withState('type', 'setType', null),
  withState('store', 'setStore', null),
  withState('module', 'setModule', null),
  withState('group', 'setGroup', null),
  withState('hasDownloadPage', 'setHasDownloadPage', false),
  withState('standOption', 'setStandOption', null),
  withState('mounted', 'setMounted', () => () => {}),
  withState('sources', 'setSources', new SourceGroup()),
  withHandlers({
    updateStandalone: props => (standalone, editingId, formSave) => {
      const payload = {
        standOption: standalone,
        params: { id: editingId },
      };

      if (formSave) {
        payload.standOption.formSave = formSave;
      }

      if (payload.standOption.pageMessage) {
        const options = payload.standOption;
        const multiline = payload.standOption.pageMessage.trim().split('\n');
        let text = '';
        for (const line of multiline) {
          text += `<span>${line}</span>`;
        }

        if (multiline.lenght >= 2) {
          text = `<span>${payload.standOption.pageMessage}</span>`;
        }

        options.pageMessage = text;
      }

      http
        .put(`/template/${editingId}`, payload)
        .then(({ data }) => {
          if (data) {
            if (data.pageMessage) {
              data.pageMessage = data.pageMessage.replace('<span>', '');
              data.pageMessage = data.pageMessage.replace('</span>', '\n');
            }

            if (data && data.pageMessage) {
              let test = data.pageMessage.replace(/<span>/g, '');
              test = test.replace(/<\/span>/g, '\n');
              data.pageMessage = test;
            }
            props.setStandOption(data);
            props.setHasDownloadPage(data.hasDownloadPage || false);
          }
        })
        .catch(err => {
          console.log(err);
        });
    },
  }),
  $lifecycle
);

export default enhance(TemplateEditor);
